import { FC, useEffect, useRef, useState } from 'react';
import type { Map as LeafletMap } from 'leaflet';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  AppSidebar,
  Background,
  Button,
  Checkbox,
  ErrorText,
  LoadingOverlay,
  Select,
} from '@/components';
import { ButtonColor, ButtonSize, Icons } from '@/enums';
import strings from '@/language/Swedish';
import useAreaDates, { DateOptionType } from '@/api/mutations/useAreaDates';
import useMapstatSearch, {
  MapstatBoundsType,
  MapstatGeoJsonType,
} from '@/api/mutations/useMapstatSearch';
import {
  areaCategoryOptions,
  housingTypeOptions,
  methodOptions,
  generateColorRange,
  generateDynamicColorRange,
  ColorStopType,
} from './mapstatConfig';
import MapstatMap from './MapstatMap';
import exportMapstatPng from './exportMapstatPng';

const PageContainer = styled.div`
  ${tw`flex h-screen`};
`;

const Main = styled.main`
  ${tw`relative flex flex-col flex-1 overflow-hidden`};
  padding: 13px;
`;

const MapContainer = styled.div`
  ${tw`relative flex-1 w-full h-full backdrop-blur-xl bg-transparent`};
`;

const FilterGroup = styled.div`
  ${tw`flex flex-col gap-1`};
`;

const FilterLabel = styled.label`
  ${tw`text-xs text-slate-600`};
`;

const CheckboxGroup = styled.div`
  ${tw`flex flex-col gap-2 py-1`};
`;

const BottomContainer = styled.div`
  ${tw`mt-auto pt-3`};
`;

interface CriteriaType {
  areaCategory: string;
  housingType: string;
  method: string;
  validAt: string;
}

const defaultCriteria: CriteriaType = {
  areaCategory: '2',
  housingType: 'apt',
  method: 'avg_recounted_psqm_n_best',
  validAt: '',
};

const Mapstat: FC = () => {
  const [criteria, setCriteria] = useState<CriteriaType>(defaultCriteria);
  const [dynamicColours, setDynamicColours] = useState(true);
  const [lockedColours, setLockedColours] = useState(false);
  const [dateOptions, setDateOptions] = useState<DateOptionType[]>([]);
  const [colorMapping, setColorMapping] = useState<ColorStopType[]>([]);
  const [geojson, setGeojson] = useState<MapstatGeoJsonType | null>(null);
  const [mapInstance, setMapInstance] = useState<LeafletMap | null>(null);

  const boundsRef = useRef<MapstatBoundsType | null>(null);
  const dynamicColoursRef = useRef(dynamicColours);
  dynamicColoursRef.current = dynamicColours;
  const lockedColoursRef = useRef(lockedColours);
  lockedColoursRef.current = lockedColours;
  const hasSearchedRef = useRef(false);

  const { mutate: loadDates, isPending: datesPending } = useAreaDates();
  const {
    mutate: searchMutation,
    isPending: searchPending,
    isError: searchError,
  } = useMapstatSearch();

  const fieldNameFor = (current: CriteriaType) =>
    `${current.method}_${current.housingType}`;

  const search = (current: CriteriaType) => {
    const bounds = boundsRef.current;
    if (!bounds || !current.validAt || searchPending) return;

    searchMutation(
      {
        fieldName: fieldNameFor(current),
        categoryId: current.areaCategory,
        validAt: current.validAt,
        bounds,
      },
      {
        onSuccess: (result) => {
          if (!result) return;

          if (!lockedColoursRef.current) {
            if (dynamicColoursRef.current) {
              const values = (result.geojson?.features ?? [])
                .map((feature) => feature?.properties?.field_value)
                .filter((value) => typeof value === 'number');
              setColorMapping(generateDynamicColorRange(values));
            } else {
              setColorMapping(
                generateColorRange(
                  result.field.colour_min_value,
                  result.field.colour_max_value,
                  result.field.colour_unit,
                  result.field.min_colour,
                  result.field.max_colour
                )
              );
            }
          }

          setGeojson(result.geojson);
        },
      }
    );
  };

  const refreshDates = (current: CriteriaType) => {
    loadDates(
      {
        fieldName: fieldNameFor(current),
        categoryId: current.areaCategory,
      },
      {
        onSuccess: (dates) => {
          setDateOptions(dates);
          if (!dates.length) return;

          const currentMonth = current.validAt.substring(0, 7);
          const stillValid = dates.some((date) => date.label === currentMonth);
          const nextCriteria = stillValid
            ? current
            : { ...current, validAt: dates[0].value };

          setCriteria(nextCriteria);
          search(nextCriteria);
        },
      }
    );
  };

  useEffect(() => {
    refreshDates(defaultCriteria);
  }, []);

  const handleCriteriaChange = (
    key: keyof CriteriaType,
    value: string | number
  ) => {
    const nextCriteria = { ...criteria, [key]: String(value) };
    setCriteria(nextCriteria);

    if (key === 'validAt') {
      search(nextCriteria);
    } else {
      // Category, housing type and method changes can shift which dates exist
      refreshDates(nextCriteria);
    }
  };

  const handleBoundsChange = (bounds: MapstatBoundsType) => {
    boundsRef.current = bounds;

    if (!hasSearchedRef.current && criteria.validAt) {
      hasSearchedRef.current = true;
      search(criteria);
    }
  };

  const minZoom = criteria.areaCategory === '3' ? 2 : 5;
  const isPending = searchPending || datesPending;

  return (
    <PageContainer>
      <Background />
      <AppSidebar currentApp="mapstat">
        <FilterGroup>
          <FilterLabel htmlFor="mapstat-area-category">
            {strings.mapstat.areaCategory}
          </FilterLabel>
          <Select
            id="mapstat-area-category"
            options={areaCategoryOptions}
            selectedValue={criteria.areaCategory}
            handleChange={(value) => handleCriteriaChange('areaCategory', value)}
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel htmlFor="mapstat-housing-type">
            {strings.mapstat.housingType}
          </FilterLabel>
          <Select
            id="mapstat-housing-type"
            options={housingTypeOptions}
            selectedValue={criteria.housingType}
            handleChange={(value) => handleCriteriaChange('housingType', value)}
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel htmlFor="mapstat-method">
            {strings.mapstat.method}
          </FilterLabel>
          <Select
            id="mapstat-method"
            options={methodOptions}
            selectedValue={criteria.method}
            handleChange={(value) => handleCriteriaChange('method', value)}
          />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel htmlFor="mapstat-valid-at">
            {strings.mapstat.date}
          </FilterLabel>
          <Select
            id="mapstat-valid-at"
            options={dateOptions}
            selectedValue={criteria.validAt}
            handleChange={(value) => handleCriteriaChange('validAt', value)}
          />
        </FilterGroup>
        <CheckboxGroup>
          <Checkbox
            id="mapstat-dynamic-colour"
            label={strings.mapstat.dynamicColour}
            isChecked={dynamicColours}
            onToggle={() => setDynamicColours((previous) => !previous)}
          />
          <Checkbox
            id="mapstat-lock-colour"
            label={strings.mapstat.lockColour}
            isChecked={lockedColours}
            onToggle={() => setLockedColours((previous) => !previous)}
          />
        </CheckboxGroup>
        {searchError && <ErrorText text={strings.mapstat.error} />}
        <BottomContainer>
          <Button
            color={ButtonColor.border}
            size={ButtonSize.fullWidth}
            icon={Icons.report}
            disabled={!geojson?.features?.length || !mapInstance}
            onClick={() => {
              if (!mapInstance || !geojson) return;
              exportMapstatPng({
                map: mapInstance,
                geojson,
                colorMapping,
                fileName: strings.mapstat.exportFileName,
              });
            }}
          >
            {strings.mapstat.exportPng}
          </Button>
        </BottomContainer>
      </AppSidebar>
      <Main>
        {isPending && <LoadingOverlay />}
        <MapContainer>
          <MapstatMap
            geojson={geojson}
            colorMapping={colorMapping}
            minZoom={minZoom}
            onBoundsChange={handleBoundsChange}
            onSearchClick={() => search(criteria)}
            onMapLoad={setMapInstance}
          />
        </MapContainer>
      </Main>
    </PageContainer>
  );
};

export default Mapstat;
